<template>
  <div>
    <!-- ─── Expanded drawer ───────────────────────────────────────── -->
    <!-- Uses BaseDrawer + DrawerSection per project convention. The
         drawer is mounted at DashboardLayout level so it persists across
         routes — operator can navigate around freely while a payment is
         in flight. -->
    <base-drawer
      :show="isExpanded"
      :title="drawerTitle"
      width="420px"
      @close="onMinimize"
    >
      <template #header-actions>
        <button
          type="button"
          class="ppos__minimize-btn"
          aria-label="Minimize"
          title="Minimize"
          @click="onMinimize"
        >
          <i class="tim-icons icon-double-right"></i>
        </button>
      </template>

      <template v-if="session">
        <drawer-section :title="statusLabel" :icon="statusIcon">
          <div class="ppos__amount">{{ formattedAmount }}</div>
          <p class="ppos__amount-caption">Awaiting capture on the device.</p>

          <div class="ppos__pulse" :class="`ppos__pulse--${session.status}`">
            <span class="ppos__pulse-dot"></span>
            <span class="ppos__pulse-label">{{ pulseLabel }}</span>
          </div>
        </drawer-section>

        <drawer-section title="Device" icon="tim-icons icon-mobile">
          <p class="ppos__row">
            <span class="ppos__row-label">Name</span>
            <span class="ppos__row-value">{{ session.deviceName }}</span>
          </p>
          <p class="ppos__row">
            <span class="ppos__row-label">Transaction</span>
            <span class="ppos__row-value">
              <a href="javascript:void(0)" @click="goToTransaction">#{{ session.orderNumber || session.transactionId }}</a>
            </span>
          </p>
          <p v-if="elapsedLabel" class="ppos__row">
            <span class="ppos__row-label">Elapsed</span>
            <span class="ppos__row-value">{{ elapsedLabel }}</span>
          </p>
        </drawer-section>

        <info-hint v-if="session.status === 'paid'" variant="success">
          Payment captured. This drawer will close shortly.
        </info-hint>
        <info-hint v-else-if="session.status === 'cancelled'" variant="neutral">
          Cancelled. The reader's prompt has been cleared.
        </info-hint>
        <info-hint v-else-if="session.status === 'expired'" variant="warning">
          The session expired before the customer tapped. Dispatch again to retry.
        </info-hint>
      </template>

      <template #footer>
        <div v-if="session" class="ppos__footer">
          <base-button
            v-if="isTerminalState"
            type="primary"
            size="sm"
            @click="onDismiss"
          >
            Done
          </base-button>
          <base-button v-else type="secondary" size="sm" :disabled="cancelling" @click="onCancel">
            {{ cancelling ? 'Cancelling...' : 'Cancel payment' }}
          </base-button>
        </div>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="cancelDialog.show"
      title="Cancel this payment?"
      message="The customer will be unable to complete the tap. The reader prompt will be cleared."
      variant="warning"
      show-cancel
      confirm-text="Cancel payment"
      cancel-text="Keep waiting"
      confirm-variant="danger"
      @confirm="confirmCancel"
      @cancel="cancelDialog.show = false"
    />

    <!-- ─── Minimized floating chip ────────────────────────────────
         Pattern mirrors the wizard side panel's minimized state
         (`OnboardingSidePanel.vue`): pink-bordered glowing pill on
         the right edge, click to expand. -->
    <transition name="fade">
      <div
        v-if="isMinimizedChip"
        class="ppos-chip"
        :class="`ppos-chip--${session.status}`"
        role="button"
        tabindex="0"
        @click="onExpand"
        @keydown.enter="onExpand"
        aria-label="POS payment in progress"
      >
        <span class="ppos-chip__dot"></span>
        <div class="ppos-chip__body">
          <p class="ppos-chip__title">{{ chipTitle }}</p>
          <p class="ppos-chip__sub">{{ session.deviceName }} · {{ formattedAmount }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import posPaymentService from '@/services/posPayment';

export default {
  name: 'PosPaymentInProgressDrawer',

  data() {
    return {
      // Ticks every second to keep the elapsed timer live without
      // having to reactify the polling itself.
      tick: 0,
      _tickInterval: null,
      // Cancel confirm (base-alert-dialog) and the in-flight guard for the
      // DELETE behind it.
      cancelDialog: { show: false },
      cancelling: false,
    };
  },

  computed: {
    session() {
      return posPaymentService.state.activeSession;
    },
    isMinimized() {
      return posPaymentService.state.isMinimized;
    },
    isExpanded() {
      return !!this.session && !this.isMinimized;
    },
    isMinimizedChip() {
      return !!this.session && this.isMinimized;
    },
    isTerminalState() {
      if (!this.session) return false;
      return ['paid', 'failed', 'cancelled', 'expired'].includes(this.session.status);
    },
    drawerTitle() {
      if (!this.session) return 'POS payment';
      const map = {
        waiting: 'Waiting for tap',
        processing: 'Processing tap',
        paid: 'Payment received',
        failed: 'Payment failed',
        cancelled: 'Payment cancelled',
        expired: 'Payment session expired',
      };
      return map[this.session.status] || 'POS payment';
    },
    statusLabel() {
      return this.drawerTitle;
    },
    statusIcon() {
      if (!this.session) return 'tim-icons icon-mobile';
      const map = {
        waiting: 'tim-icons icon-watch-time',
        processing: 'tim-icons icon-refresh-01',
        paid: 'tim-icons icon-check-2',
        failed: 'tim-icons icon-alert-circle-exc',
        cancelled: 'tim-icons icon-simple-remove',
        expired: 'tim-icons icon-alert-circle-exc',
      };
      return map[this.session.status] || 'tim-icons icon-mobile';
    },
    pulseLabel() {
      if (!this.session) return '';
      const map = {
        waiting: 'Waiting for customer to tap',
        processing: 'Card detected — processing',
        paid: 'Captured',
        failed: 'Could not capture',
        cancelled: 'Cancelled',
        expired: 'Session expired',
      };
      return map[this.session.status] || '';
    },
    chipTitle() {
      if (!this.session) return '';
      const map = {
        waiting: 'Waiting for tap',
        processing: 'Processing tap',
        paid: 'Payment received',
        failed: 'Payment failed',
        cancelled: 'Cancelled',
        expired: 'Expired',
      };
      return map[this.session.status] || 'POS payment';
    },
    formattedAmount() {
      if (!this.session || this.session.amount == null) return '';
      const n = Number(this.session.amount);
      if (Number.isNaN(n)) return '';
      // amount is in dollars; same shape as the rest of the editor uses
      // raw $ for now (group-currency formatting can come later via the
      // venue settings hook).
      return `$${n.toFixed(2)}`;
    },
    elapsedLabel() {
      // referencing this.tick forces re-eval each second
      void this.tick;
      if (!this.session || !this.session.startedAt) return '';
      const secs = Math.floor((Date.now() - this.session.startedAt) / 1000);
      if (secs < 60) return `${secs}s`;
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}m ${s}s`;
    },
  },

  mounted() {
    this._tickInterval = setInterval(() => { this.tick += 1; }, 1000);
  },

  beforeDestroy() {
    if (this._tickInterval) clearInterval(this._tickInterval);
  },

  methods: {
    onMinimize() {
      posPaymentService.minimize();
    },
    onExpand() {
      posPaymentService.expand();
    },
    onDismiss() {
      posPaymentService.dismiss();
    },
    onCancel() {
      if (this.cancelling) return;
      this.cancelDialog.show = true;
    },
    async confirmCancel() {
      this.cancelDialog.show = false;
      if (this.cancelling) return;
      this.cancelling = true;
      try {
        await posPaymentService.cancel();
      } catch (err) {
        // The server refused the cancel (tap in progress, or the payment is
        // being recorded); the session stays as it is.
        // axios has already parsed a JSON body into an object; a string body
        // is a legacy plain-text error.
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        this.$notify({ type: 'warning', message: parsed || 'Could not cancel the payment' });
      } finally {
        this.cancelling = false;
      }
    },
    goToTransaction() {
      if (!this.session) return;
      // Same URL pattern TransactionDetails uses elsewhere.
      this.$router.push({
        path: '/purchases/transaction-details',
        query: { id: this.session.transactionId },
      });
      posPaymentService.minimize();
    },
  },
};
</script>

<style lang="scss" scoped>
// ─── Drawer body bits ────────────────────────────────────────────────
// Hide BaseDrawer's default X close button — we render our own chevron
// in the header-actions slot to signal "minimize" (slide off-screen)
// rather than "close" (destroy state).
::v-deep .base-drawer__close-btn {
  display: none;
}

.ppos__minimize-btn {
  appearance: none;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-size: 18px;
  line-height: 1;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover,
  &:focus-visible {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.08);
  }
}

.ppos__amount {
  font-size: 32px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
  margin: 4px 0 2px;
}
.ppos__amount-caption {
  margin: 0 0 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.ppos__pulse {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(125, 125, 125, 0.08);
  border: 1px solid rgba(125, 125, 125, 0.18);
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.85);

  &--paid {
    background: rgba(0, 242, 195, 0.08);
    border-color: rgba(0, 242, 195, 0.3);
  }
  &--failed,
  &--expired {
    background: rgba(253, 93, 147, 0.08);
    border-color: rgba(253, 93, 147, 0.3);
  }
  &--cancelled {
    opacity: 0.7;
  }
}
.ppos__pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e14eca;
  flex-shrink: 0;
  animation: ppos-pulse 1.5s ease-in-out infinite;

  .ppos__pulse--paid & {
    background: #00f2c3;
    animation: none;
  }
  .ppos__pulse--failed &,
  .ppos__pulse--expired & {
    background: #fd5d93;
    animation: none;
  }
  .ppos__pulse--cancelled & {
    background: rgba(255, 255, 255, 0.4);
    animation: none;
  }
}

@keyframes ppos-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.45; transform: scale(1.18); }
}

.ppos__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 8px 0;
  font-size: 12.5px;
}
.ppos__row-label {
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  font-size: 10.5px;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.ppos__row-value {
  color: #fff;
  a {
    color: #e14eca;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}

.ppos__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

// ─── Minimized chip ──────────────────────────────────────────────────
.ppos-chip {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1049;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(30, 30, 47, 0.98), rgba(20, 20, 35, 0.98));
  border: 1px solid rgba(225, 78, 202, 0.4);
  cursor: pointer;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.45),
    0 2px 10px rgba(225, 78, 202, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: rgba(225, 78, 202, 0.65);
    box-shadow:
      0 12px 28px rgba(0, 0, 0, 0.55),
      0 3px 14px rgba(225, 78, 202, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  &--paid {
    border-color: rgba(0, 242, 195, 0.5);
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.45),
      0 2px 10px rgba(0, 242, 195, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }
  &--failed,
  &--expired {
    border-color: rgba(253, 93, 147, 0.5);
  }
}

.ppos-chip__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e14eca;
  flex-shrink: 0;
  animation: ppos-pulse 1.5s ease-in-out infinite;

  .ppos-chip--paid & {
    background: #00f2c3;
    animation: none;
  }
  .ppos-chip--failed &,
  .ppos-chip--expired & {
    background: #fd5d93;
    animation: none;
  }
  .ppos-chip--cancelled & {
    background: rgba(255, 255, 255, 0.4);
    animation: none;
  }
}

.ppos-chip__body {
  display: flex;
  flex-direction: column;
  text-align: left;
}
.ppos-chip__title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.2;
}
.ppos-chip__sub {
  margin: 1px 0 0;
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.2;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Light-mode parity (unscoped because BaseDrawer applies the theme class
   higher up; scoped selectors don't traverse the slot boundary the way
   the parent's white-content class needs). */
.white-content .ppos-chip {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 245, 250, 0.98));
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 2px 10px rgba(225, 78, 202, 0.18);
}
.white-content .ppos-chip__title { color: rgba(0, 0, 0, 0.85); }
.white-content .ppos-chip__sub { color: rgba(0, 0, 0, 0.55); }
.white-content .ppos__amount { color: rgba(0, 0, 0, 0.9); }
.white-content .ppos__amount-caption { color: rgba(0, 0, 0, 0.55); }
.white-content .ppos__pulse {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.85);
}
.white-content .ppos__row-label { color: rgba(0, 0, 0, 0.55); }
.white-content .ppos__row-value { color: rgba(0, 0, 0, 0.85); }
.white-content .ppos__minimize-btn { color: rgba(0, 0, 0, 0.5); }
.white-content .ppos__minimize-btn:hover { color: #e14eca; background: rgba(225, 78, 202, 0.06); }
</style>
